import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { create } from 'zustand';

import type { CallSessionPayload } from '#features/calls/model/calls';
import {
  useCallStore,
  type CallConnectionState,
  type CallPhase,
} from '#features/calls/model/callStore';
import { toCallAvatarUrl } from '#features/calls/utils/callAvatar';
import { callBreadcrumb, msSince, reportCallIncident } from '#features/calls/utils/callDiagnostics';
import { getClientSessionId, setNativeClientSessionId } from '#features/calls/utils/clientSession';
import { isCallDismissedHere } from '#features/calls/utils/dismissedCalls';
import { useVoiceStore } from '#features/voice/model/voiceStore';
import { envConfig } from '#constants/envConfig';

export type NativeCallSnapshot = {
  sessionId: string;
  revision?: number;
  enabled: boolean;
  phase: CallPhase;
  call: CallSessionPayload | null;
  peer: { displayName: string | null; avatar: string | null } | null;
  media: {
    connectionState: CallConnectionState;
    connectedAt: number | null;
    micDenied: boolean;
    peerConnected: boolean;
    muted: boolean;
  };
  endReason: string | null;
  error: string | null;
  interruptedVoiceSessionId?: string | null;
  interruptedVoiceCallId?: string | null;
};

interface NativeCallsPlugin {
  getState(): Promise<NativeCallSnapshot>;
  syncAuth(options: { voiceSessionId?: string | null }): Promise<NativeCallSnapshot>;
  signOut(): Promise<NativeCallSnapshot>;
  resync(): Promise<NativeCallSnapshot>;
  reportIncoming(options: {
    callId: string;
    callerId: string;
    calleeId: string;
    callerName?: string;
    expiresAt?: number;
  }): Promise<NativeCallSnapshot>;
  adoptOutgoing(options: {
    call: CallSessionPayload;
    peer?: { displayName: string | null; avatar: string | null };
  }): Promise<NativeCallSnapshot>;
  answer(options: { callId: string }): Promise<NativeCallSnapshot>;
  end(options: { callId: string }): Promise<NativeCallSnapshot>;
  claim(options: { callId: string }): Promise<NativeCallSnapshot>;
  setMuted(options: { callId: string; muted: boolean }): Promise<NativeCallSnapshot>;
  recordDiagnostic?(options: {
    event: string;
    fields: Record<string, string | number | boolean>;
  }): Promise<void>;
  addListener(
    event: 'stateChanged',
    listener: (state: NativeCallSnapshot) => void
  ): Promise<PluginListenerHandle>;
}

/** Build flag and native plugin must agree. Older shells retain their existing media path. */
export const isNativeCallEnabled = (): boolean =>
  envConfig.IS_NATIVE &&
  envConfig.IOS_NATIVE_CALLS &&
  Capacitor.getPlatform() === 'ios' &&
  Capacitor.isPluginAvailable('NativeCalls') &&
  useNativeCallSnapshot.getState().snapshot?.enabled !== false;

// Never resolve a Capacitor proxy from a Promise: its synthetic `then` would bridge to native.
const plugin = registerPlugin<NativeCallsPlugin>('NativeCalls');

/** Opt-in debug metadata only. The native writer filters fields and never logs credentials. */
export const reportNativeCallDiagnostic = (
  event: string,
  fields: Record<string, string | number | boolean> = {}
): void => {
  if (
    import.meta.env.VITE_IOS_CALL_DIAGNOSTICS !== 'true' ||
    !envConfig.IS_NATIVE ||
    Capacitor.getPlatform() !== 'ios' ||
    !Capacitor.isPluginAvailable('NativeCalls')
  )
    return;
  try {
    void plugin.recordDiagnostic?.({ event, fields })?.catch(() => undefined);
  } catch {
    /* Older shells and failed logging must not affect the call. */
  }
};
export const useNativeCallSnapshot = create<{ snapshot: NativeCallSnapshot | null }>(() => ({
  snapshot: null,
}));

const rememberedInterruptions = new Set<string>();

export const applyNativeCallSnapshot = (snapshot: NativeCallSnapshot): void => {
  const previousUIPhase = useCallStore.getState().phase;
  const diagnosticFields = {
    uiPhase: previousUIPhase,
    snapshotPhase: snapshot.phase,
    receivedRevision: snapshot.revision ?? 0,
    callMatches: snapshot.call?.id === useCallStore.getState().call?.callId,
  };
  reportNativeCallDiagnostic('ui.snapshot.received', diagnosticFields);
  const previous = useNativeCallSnapshot.getState().snapshot;
  if (
    snapshot.revision !== undefined &&
    previous?.revision !== undefined &&
    snapshot.revision < previous.revision
  ) {
    reportNativeCallDiagnostic('ui.snapshot.skipped', diagnosticFields);
    return;
  }
  useNativeCallSnapshot.setState({ snapshot });
  if (!snapshot.enabled) return;
  setNativeClientSessionId(snapshot.sessionId);
  if (snapshot.interruptedVoiceSessionId) {
    const voice = useVoiceStore.getState();
    const interrupted = snapshot.interruptedVoiceSessionId;
    const key = `${snapshot.interruptedVoiceCallId}:${interrupted}`;
    if (voice.room?.sessionId === snapshot.interruptedVoiceSessionId) {
      voice.rememberInterruptedRoom(voice.room);
      voice.reset();
      rememberedInterruptions.add(key);
    } else if (
      snapshot.call?.id === snapshot.interruptedVoiceCallId &&
      !rememberedInterruptions.has(key)
    ) {
      // Back to voice needs only this id; join fetches fresh room credentials.
      voice.rememberInterruptedRoom({
        sessionId: interrupted,
        postId: '',
        hostId: null,
        livekitUrl: '',
        token: '',
        maxParticipants: null,
      });
      rememberedInterruptions.add(key);
    }
    voice.forgetVoiceSession();
  }
  const store = useCallStore.getState();
  const session = snapshot.call;
  if (!session) {
    if (snapshot.phase === 'idle' && store.call?.callId && store.phase !== 'ended') {
      store.setEnded('ended');
    }
    reportNativeCallDiagnostic('ui.snapshot.applied', {
      previousUIPhase,
      uiPhase: useCallStore.getState().phase,
    });
    return;
  }
  // Late end/sync callbacks cannot replace a newer call being dialled in the app.
  if (snapshot.phase === 'ended' && store.call?.callId !== session.id) return;
  const same = store.call?.callId === session.id ? store.call : null;
  const info = {
    callId: session.id,
    chatId: session.chatId,
    roomName: session.roomName,
    peer: {
      userId: session.peerUserId,
      displayName: snapshot.peer?.displayName ?? same?.peer.displayName ?? null,
      avatar: toCallAvatarUrl(snapshot.peer?.avatar) ?? same?.peer.avatar ?? null,
    },
    startedByMe: session.peerUserId === session.calleeId,
    withVideo: false,
    livekitUrl: session.livekitUrl,
    token: session.token,
    acceptedAt: same?.acceptedAt ?? snapshot.media.connectedAt,
    ringingSince: same?.ringingSince ?? (Date.parse(session.createdAt ?? '') || Date.now()),
  };
  if (snapshot.phase === 'incoming') {
    store.setIncoming(info);
  } else if (snapshot.phase === 'outgoing') {
    if (!same || store.phase !== 'outgoing') store.setOutgoing(info);
  } else if (snapshot.phase === 'active') {
    if (store.phase !== 'active') {
      callBreadcrumb('call accepted', {
        callId: session.id,
        role: info.startedByMe ? 'caller' : 'callee',
        via: 'native',
      });
    }
    if (!same) store.setOutgoing(info);
    store.setActive({ token: session.token, livekitUrl: session.livekitUrl });
    useCallStore.setState({ micEnabled: !snapshot.media.muted, camEnabled: false });
    useCallStore.getState().setMedia(session.id, {
      connectionState: snapshot.media.connectionState,
      connectedAt: snapshot.media.connectedAt,
      micDenied: snapshot.media.micDenied,
      peerConnected: snapshot.media.peerConnected,
    });
  } else if (snapshot.phase === 'elsewhere') {
    if (isCallDismissedHere(session.id)) store.reset();
    else store.setElsewhere({ ...info, token: null });
  } else if (snapshot.phase === 'ended') {
    reportNativeCallerNeverActive(snapshot, store);
    store.setEnded(snapshot.endReason ?? 'ended');
  }
  reportNativeCallDiagnostic('ui.snapshot.applied', {
    previousUIPhase,
    uiPhase: useCallStore.getState().phase,
  });
};

/**
 * Сколько звонок должен пробыть в разговоре без медиа, чтобы его конец считался инцидентом. Соединение
 * штатно занимает 1-3 с, и конец за это время - обычно чей-то сброс: звонящий передумал, пока шло
 * соединение, или собеседник ответил и сразу положил. Различить сброс и обрыв по снимку нельзя:
 * нативный слой называет `ended` и свой отбой, и отбой собеседника. Остаётся порог по времени.
 */
const MEDIA_NEVER_CONNECTED_MIN_MS = 5_000;

/**
 * Звонящий на iOS так и не разговаривал. Два вида с разными кодами, чтобы GlitchTip не сливал их в
 * одну проблему. `caller_never_active` - сервер закрыл принятый звонок, а снимок всё в наборе
 * (ENDED сервер ставит только принятому, это тот же случай, что веб-ветка в `applyWsCallEvent`).
 * `media_never_connected` - снимок перешёл в разговор, но `connectedAt` в сторе так и остался
 * пустым. Финальный снимок `connectedAt` обнуляет, поэтому смотрим в стор, а не в него.
 */
const reportNativeCallerNeverActive = (
  snapshot: NativeCallSnapshot,
  store: ReturnType<typeof useCallStore.getState>
): void => {
  const call = store.call;
  if (!call?.startedByMe) return;
  if (store.phase === 'outgoing' && snapshot.call?.status === 'ENDED') {
    reportCallIncident('caller_never_active', {
      callId: call.callId,
      role: 'caller',
      stage: 'native-outgoing-ended',
      msInOutgoing: msSince(call.ringingSince),
      endReason: snapshot.endReason,
    });
  } else if (store.phase === 'active' && store.media.connectedAt === null) {
    const sinceAnswer = msSince(call.acceptedAt);
    // Время неизвестно - не глушим: лучше лишний отчёт, чем пропущенный обрыв.
    if (sinceAnswer !== null && sinceAnswer < MEDIA_NEVER_CONNECTED_MIN_MS) return;
    reportCallIncident('media_never_connected', {
      callId: call.callId,
      role: 'caller',
      stage: 'native-media-never-connected',
      msSinceAnswer: sinceAnswer,
      connectionState: store.media.connectionState,
      endReason: snapshot.endReason,
    });
  }
};

const apply = async (request: Promise<NativeCallSnapshot>): Promise<void> => {
  applyNativeCallSnapshot(await request);
};

export const getCallClientSessionId = async (): Promise<string> => {
  if (isNativeCallEnabled()) {
    const snapshot = await plugin.getState();
    applyNativeCallSnapshot(snapshot);
    if (snapshot.enabled) setNativeClientSessionId(snapshot.sessionId);
  }
  return getClientSessionId();
};

export const syncNativeCallAuth = async (voiceSessionId?: string | null): Promise<void> => {
  if (isNativeCallEnabled()) await apply(plugin.syncAuth({ voiceSessionId }));
};
export const stopNativeCallsForSignOut = async (): Promise<void> => {
  if (!isNativeCallEnabled()) return;
  try {
    await apply(plugin.signOut());
  } finally {
    useCallStore.getState().reset();
  }
};
export const resyncNativeCall = async (): Promise<void> => {
  reportNativeCallDiagnostic('ui.resync.request', { uiPhase: useCallStore.getState().phase });
  try {
    await apply(plugin.resync());
  } catch (error) {
    reportNativeCallDiagnostic('ui.resync.failure', { errorCategory: 'bridge' });
    throw error;
  }
};
export const adoptNativeOutgoing = (
  call: CallSessionPayload,
  peer: { displayName: string | null; avatar: string | null }
): Promise<void> => {
  reportNativeCallDiagnostic('ui.outgoing.request', { uiPhase: useCallStore.getState().phase });
  return apply(plugin.adoptOutgoing({ call, peer })).then(() => {
    reportNativeCallDiagnostic('ui.outgoing.returned', { uiPhase: useCallStore.getState().phase });
    return undefined;
  });
};
export const answerNativeCall = (callId: string): Promise<void> => apply(plugin.answer({ callId }));
export const endNativeCall = (callId: string): Promise<void> => apply(plugin.end({ callId }));
export const claimNativeCall = (callId: string): Promise<void> => apply(plugin.claim({ callId }));
export const muteNativeCall = (callId: string, muted: boolean): Promise<void> =>
  apply(plugin.setMuted({ callId, muted }));

/** Polling cannot overwrite a pending UI tap: command the native media owner directly. */
export const setCallMicrophoneEnabled = (enabled: boolean): void => {
  const state = useCallStore.getState();
  if (isNativeCallEnabled() && state.call && state.phase === 'active') {
    void muteNativeCall(state.call.callId, !enabled).catch(() => undefined);
  } else state.setMicEnabled(enabled);
};

export const receiveNativeIncoming = (
  options: Parameters<NativeCallsPlugin['reportIncoming']>[0]
): Promise<void> => apply(plugin.reportIncoming(options));
export const subscribeNativeCalls = async (): Promise<() => void> => {
  const handle = await plugin.addListener('stateChanged', applyNativeCallSnapshot);
  reportNativeCallDiagnostic('ui.subscription.ready');
  try {
    applyNativeCallSnapshot(await plugin.getState());
  } catch (error) {
    reportNativeCallDiagnostic('ui.subscription.failure', { errorCategory: 'bridge' });
    await handle.remove();
    throw error;
  }
  return () => {
    reportNativeCallDiagnostic('ui.subscription.removed');
    void handle.remove();
  };
};
